import { createContext, FC, useState } from "react";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { HighlightedRegionSetSpecialtyGroupsWithGapsAndCount, ProviderHubFilterState, ProviderHubServicingProviders, ProviderHubServicingProvidersTableData, RegionSetSpecialtyGroupsWithGaps } from "../interfaces";
import { PaginationResult } from "@app/shared/waffles/exports";

/* eslint-disable sort-keys */
interface ProviderHubContextProps {
  availableSpecialties: Array<DropdownItem<string>>,
  displayGapsAndProvidersTable: boolean,
  highlightedRegionSetSpecialtyGroupsWithGapsAndCount: HighlightedRegionSetSpecialtyGroupsWithGapsAndCount[],
  highlightedServicingProviders: string[],
  pageIndex: number;
  pageSize: number;
  providersFilters: ProviderHubFilterState,
  providerHubServicingProviders: PaginationResult<ProviderHubServicingProviders>,
  providerHubServicingProviderAccessPointData: Map<string, ProviderHubServicingProvidersTableData[]>,
  regionSetSpecialtyGroupsWithGaps: RegionSetSpecialtyGroupsWithGaps[],
  resetToFirstPage: boolean,
  selectedServicingProviders: string[],
  selectedRegionSetSpecialtyGroupsWithGaps: string[],
  selectedServiceAreas: string[],
  selectedSpecialties: Array<DropdownItem<string>>,
  triggerProvidersRefreshData: boolean,

  setAvailableSpecialties: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>,
  setDisplayGapsAndProvidersTable: React.Dispatch<React.SetStateAction<boolean>>,
  setHighlightedServicingProviders: React.Dispatch<React.SetStateAction<string[]>>,
  setHighlightedRegionSetSpecialtyGroupsWithGapsAndCount: React.Dispatch<React.SetStateAction<HighlightedRegionSetSpecialtyGroupsWithGapsAndCount[]>>,
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setProvidersFilters: React.Dispatch<React.SetStateAction<ProviderHubFilterState>>;
  setProviderHubServicingProviderAccessPointData: React.Dispatch<React.SetStateAction<Map<string, ProviderHubServicingProvidersTableData[]>>>;
  setProviderHubServicingProviders: React.Dispatch<React.SetStateAction<PaginationResult<ProviderHubServicingProviders>>>;
  setRegionSetSpecialtyGroupsWithGaps:  React.Dispatch<React.SetStateAction<RegionSetSpecialtyGroupsWithGaps[]>>,
  setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedServicingProviders: React.Dispatch<React.SetStateAction<string[]>>,
  setSelectedRegionSetSpecialtyGroupsWithGaps:  React.Dispatch<React.SetStateAction<string[]>>,
  setSelectedServiceAreas: React.Dispatch<React.SetStateAction<string[]>>,
  setSelectedSpecialties: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>,
  setTriggerProvidersRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProviderHubContext = createContext<Partial<ProviderHubContextProps>>({
    availableSpecialties: [],
    displayGapsAndProvidersTable: false,
    highlightedRegionSetSpecialtyGroupsWithGapsAndCount: [],
    highlightedServicingProviders: [],
    selectedServiceAreas: [],
    pageIndex: null,
    pageSize: null,
    providersFilters: {},
    providerHubServicingProviderAccessPointData: null,
    providerHubServicingProviders: { totalCount: 0, items: []},
    resetToFirstPage: null,
    regionSetSpecialtyGroupsWithGaps: [],
    selectedServicingProviders: [],
    selectedRegionSetSpecialtyGroupsWithGaps: [],
    selectedSpecialties: [],
    triggerProvidersRefreshData: false,

    setAvailableSpecialties: () => null,
    setHighlightedRegionSetSpecialtyGroupsWithGapsAndCount: () => null,
    setHighlightedServicingProviders: () => null,
    setDisplayGapsAndProvidersTable: () => null,
    setPageIndex: () => null,
    setPageSize: () => null,
    setProvidersFilters: () => null,
    setProviderHubServicingProviderAccessPointData: () => null,
    setProviderHubServicingProviders: () => null,
    setRegionSetSpecialtyGroupsWithGaps: () => null,
    setResetToFirstPage: () => null,
    setSelectedServicingProviders: () => null,
    setSelectedRegionSetSpecialtyGroupsWithGaps: () => null,
    setSelectedServiceAreas: () => null,
    setSelectedSpecialties: () => null,
    setTriggerProvidersRefreshData: () => null
});

const ProviderHubContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
 
  // Values related to the elements at the top the provider hub page
  const [selectedServiceAreas, setSelectedServiceAreas] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<Array<DropdownItem<string>>>([]);
  const [availableSpecialties, setAvailableSpecialties] = useState<Array<DropdownItem<string>>>([]);

  // Values related to the elements inside the gaps and providers views
  const [selectedRegionSetSpecialtyGroupsWithGaps, setSelectedRegionSetSpecialtyGroupsWithGaps] = useState<string[]>([])
  const [regionSetSpecialtyGroupsWithGaps, setRegionSetSpecialtyGroupsWithGaps] = useState<RegionSetSpecialtyGroupsWithGaps[]>([]);
  const [providerHubServicingProviders, setProviderHubServicingProviders] = useState<PaginationResult<ProviderHubServicingProviders>>({ totalCount: 0, items: []});
  const [selectedServicingProviders, setSelectedServicingProviders] = useState<string[]>([]);
  const [highlightedServicingProviders, setHighlightedServicingProviders] = useState<string[]>([]);
  const [highlightedRegionSetSpecialtyGroupsWithGapsAndCount, setHighlightedRegionSetSpecialtyGroupsWithGapsAndCount] = useState<HighlightedRegionSetSpecialtyGroupsWithGapsAndCount[]>([]);
  const [providerHubServicingProviderAccessPointData, setProviderHubServicingProviderAccessPointData] =  useState<Map<string, ProviderHubServicingProvidersTableData[]>>(new Map());

  // Used for pagination in the provides views
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);

  // Miscellaneous
  const [displayGapsAndProvidersTable, setDisplayGapsAndProvidersTable] =  useState<boolean>(false);
  const [providersFilters, setProvidersFilters] = useState<ProviderHubFilterState>({});
  const [triggerProvidersRefreshData, setTriggerProvidersRefreshData] = useState<boolean>(false);

  const contextValue: ProviderHubContextProps = {
    availableSpecialties,
    displayGapsAndProvidersTable,
    highlightedRegionSetSpecialtyGroupsWithGapsAndCount,
    highlightedServicingProviders,
    pageIndex,
    pageSize,
    providersFilters,
    providerHubServicingProviders,
    providerHubServicingProviderAccessPointData,
    regionSetSpecialtyGroupsWithGaps,
    resetToFirstPage,
    selectedServicingProviders,
    selectedRegionSetSpecialtyGroupsWithGaps,
    selectedServiceAreas,
    selectedSpecialties,
    triggerProvidersRefreshData,
    setAvailableSpecialties,
    setDisplayGapsAndProvidersTable,
    setHighlightedRegionSetSpecialtyGroupsWithGapsAndCount,
    setHighlightedServicingProviders,
    setPageIndex,
    setPageSize,
    setProvidersFilters,
    setProviderHubServicingProviders,
    setProviderHubServicingProviderAccessPointData,
    setSelectedServicingProviders,
    setRegionSetSpecialtyGroupsWithGaps,
    setResetToFirstPage,
    setSelectedRegionSetSpecialtyGroupsWithGaps,
    setSelectedServiceAreas,
    setSelectedSpecialties,
    setTriggerProvidersRefreshData
  
  };

  /* eslint-enable sort-keys */

  return (
    <ProviderHubContext.Provider value={contextValue}>
      {props.children}
    </ProviderHubContext.Provider>
  );
};

export { ProviderHubContext, ProviderHubContextProvider }